import React, { useState, useEffect } from 'react';
import { photoAPI } from '../../services/api';
import { X, Tag as TagIcon, Plus, Minus, Search } from 'lucide-react';

const TagManagementModal = ({ isOpen, onClose, photoIds = [], onSuccess }) => {
  const [availableTags, setAvailableTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTagsToAdd, setSelectedTagsToAdd] = useState([]);
  const [tagsToRemove, setTagsToRemove] = useState([]);
  const [newTagName, setNewTagName] = useState('');
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    if (isOpen) {
      fetchTags();
      if (photoIds.length === 1) {
        fetchPhotoTags(photoIds[0]);
      }
    }
  }, [isOpen, photoIds]);

  const fetchTags = async () => {
    try {
      const response = await photoAPI.getAllTags();
      if (response.data.success) {
        setAvailableTags(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const fetchPhotoTags = async (photoId) => {
    try {
      const response = await photoAPI.getById(photoId);
      if (response.data.success && response.data.data.tags) {
        setPhotos([response.data.data]);
      }
    } catch (error) {
      console.error('Error fetching photo tags:', error);
    }
  };

  // Sort tags alphabetically
  const sortedAvailableTags = [...availableTags].sort((a, b) => 
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  );

  const filteredTags = sortedAvailableTags.filter(tag =>
    tag.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
    !selectedTagsToAdd.includes(tag.name) &&
    !tagsToRemove.includes(tag.name)
  );

  const handleAddTag = (tagName) => {
    if (tagName && !selectedTagsToAdd.includes(tagName)) {
      setSelectedTagsToAdd([...selectedTagsToAdd, tagName]);
      setSearchQuery('');
      setNewTagName('');
    }
  };

  const handleRemoveTagFromAdd = (tagName) => {
    setSelectedTagsToAdd(selectedTagsToAdd.filter(t => t !== tagName));
  };

  const handleAddToRemove = (tagName) => {
    if (!tagsToRemove.includes(tagName)) {
      setTagsToRemove([...tagsToRemove, tagName]);
    }
  };

  const handleRemoveFromRemove = (tagName) => {
    setTagsToRemove(tagsToRemove.filter(t => t !== tagName));
  };

  const handleSave = async () => {
    if (photoIds.length === 0) {
      alert('No photos selected');
      return;
    }

    setLoading(true);
    try {
      // Add tags
      if (selectedTagsToAdd.length > 0) {
        await photoAPI.bulkAddTags(photoIds, selectedTagsToAdd);
      }

      // Remove tags
      if (tagsToRemove.length > 0) {
        await photoAPI.bulkRemoveTags(photoIds, tagsToRemove);
      }

      if (onSuccess) {
        onSuccess();
      }
      onClose();
      setSelectedTagsToAdd([]);
      setTagsToRemove([]);
      setSearchQuery('');
      setNewTagName('');
    } catch (error) {
      console.error('Error updating tags:', error);
      alert('Failed to update tags. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const existingTags = photos.length > 0 && photos[0].tags ? photos[0].tags : [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">
            Manage Tags {photoIds.length > 1 && `(${photoIds.length} photos)`}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Existing Tags (for single photo) */}
        {photoIds.length === 1 && existingTags.length > 0 && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current Tags
            </label>
            <div className="flex flex-wrap gap-2">
              {existingTags.map((tag) => (
                <span
                  key={tag}
                  className={`px-3 py-1 rounded-full text-sm flex items-center gap-2 ${
                    tagsToRemove.includes(tag)
                      ? 'bg-red-100 text-red-700 line-through'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  <TagIcon className="w-3 h-3" />
                  {tag}
                  {!tagsToRemove.includes(tag) && (
                    <button
                      onClick={() => handleAddToRemove(tag)}
                      className="hover:text-red-600"
                      title="Remove tag"
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                  )}
                  {tagsToRemove.includes(tag) && (
                    <button
                      onClick={() => handleRemoveFromRemove(tag)}
                      className="hover:text-gray-900"
                      title="Cancel removal"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Add Tags Section */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Add Tags
          </label>
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search or type new tag..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && searchQuery.trim()) {
                  handleAddTag(searchQuery.trim());
                }
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Tag Suggestions - sorted alphabetically */}
          {searchQuery && filteredTags.length > 0 && (
            <div className="border border-gray-200 rounded-lg p-2 max-h-40 overflow-y-auto mb-2">
              {filteredTags.slice(0, 20).map((tag) => (
                <button
                  key={tag.id}
                  onClick={() => handleAddTag(tag.name)}
                  className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <TagIcon className="w-4 h-4 text-gray-500" />
                    <span>{tag.name}</span>
                  </div>
                  <span className="text-xs text-gray-500">({tag.usageCount || 0})</span>
                </button>
              ))}
            </div>
          )}

          {/* Selected Tags to Add */}
          {selectedTagsToAdd.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {selectedTagsToAdd.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center gap-2"
                >
                  <TagIcon className="w-3 h-3" />
                  {tag}
                  <button
                    onClick={() => handleRemoveTagFromAdd(tag)}
                    className="hover:text-blue-900"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Popular Tags (when no search) - sorted alphabetically */}
          {!searchQuery && sortedAvailableTags.length > 0 && (
            <div className="mt-2">
              <p className="text-xs text-gray-500 mb-2">Available tags (alphabetical):</p>
              <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
                {sortedAvailableTags.map((tag) => (
                  <button
                    key={tag.id}
                    onClick={() => handleAddTag(tag.name)}
                    disabled={selectedTagsToAdd.includes(tag.name)}
                    className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${
                      selectedTagsToAdd.includes(tag.name)
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <TagIcon className="w-3 h-3" />
                    {tag.name}
                    <span className="text-gray-500">({tag.usageCount || 0})</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Summary */}
        {(selectedTagsToAdd.length > 0 || tagsToRemove.length > 0) && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm font-medium text-blue-900 mb-1">Summary:</p>
            {selectedTagsToAdd.length > 0 && (
              <p className="text-xs text-blue-700">
                Adding {selectedTagsToAdd.length} tag{selectedTagsToAdd.length !== 1 ? 's' : ''}: {selectedTagsToAdd.join(', ')}
              </p>
            )}
            {tagsToRemove.length > 0 && (
              <p className="text-xs text-red-700">
                Removing {tagsToRemove.length} tag{tagsToRemove.length !== 1 ? 's' : ''}: {tagsToRemove.join(', ')}
              </p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex space-x-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={loading || (selectedTagsToAdd.length === 0 && tagsToRemove.length === 0)}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : 'Save Tags'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TagManagementModal;

